// Contentful CMS Client Configuration
import { createClient } from 'contentful';

// Initialize Contentful client
const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
  environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master',
});

export const contentfulClient = client;

// Content type IDs - these should match your Contentful content model
export const CONTENT_TYPES = {
  VIDEO: 'video',
  BOOK: 'book',
  BLOG_POST: 'blogPost',
  MARKETPLACE_ITEM: 'marketplaceItem',
  SITE_CONFIG: 'siteConfig',
  ABOUT: 'about',
  TOPIC: 'topic',
  AWARD: 'award',
  SERVICE: 'service',
};

/**
 * Fetch all entries of a specific content type
 * @param {string} contentType - The content type ID
 * @param {object} options - Additional query options
 * @returns {Promise<Array>} - Array of entries
 */
export const fetchEntries = async (contentType, options = {}) => {
  try {
    const entries = await client.getEntries({
      content_type: contentType,
      include: 2, // Resolve up to 2 levels of linked content
      ...options,
    });
    return entries.items;
  } catch (error) {
    console.error(`Error fetching ${contentType} entries:`, error);
    throw error;
  }
};

/**
 * Fetch a single entry by ID
 * @param {string} entryId - The entry ID
 * @returns {Promise<object>} - The entry
 */
export const fetchEntryById = async (entryId) => {
  try {
    const entry = await client.getEntry(entryId);
    return entry;
  } catch (error) {
    console.error(`Error fetching entry ${entryId}:`, error);
    throw error;
  }
};

/**
 * Fetch a single entry by slug field
 * @param {string} contentType - The content type ID
 * @param {string} slug - The slug value
 * @returns {Promise<object|null>} - The entry or null
 */
export const fetchEntryBySlug = async (contentType, slug) => {
  try {
    const entries = await client.getEntries({
      content_type: contentType,
      'fields.slug': slug,
      include: 2,
      limit: 1,
    });
    return entries.items[0] || null;
  } catch (error) {
    console.error(`Error fetching ${contentType} by slug:`, error);
    throw error;
  }
};

/**
 * Search entries with text query
 * @param {string} contentType - The content type ID
 * @param {string} query - Search query
 * @param {object} options - Additional query options
 * @returns {Promise<object>} - Search results with items and total
 */
export const searchEntries = async (contentType, query, options = {}) => {
  try {
    const entries = await client.getEntries({
      content_type: contentType,
      query: query,
      include: 2,
      ...options,
    });
    return {
      items: entries.items,
      total: entries.total,
      skip: entries.skip,
      limit: entries.limit,
    };
  } catch (error) {
    console.error('Error searching entries:', error);
    throw error;
  }
};

/**
 * Fetch entries filtered by a specific field value
 * @param {string} contentType - The content type ID
 * @param {string} field - The field name
 * @param {string} value - The field value
 * @param {object} options - Additional query options
 * @returns {Promise<Array>} - Array of entries
 */
export const fetchEntriesByField = async (contentType, field, value, options = {}) => {
  try {
    const entries = await client.getEntries({
      content_type: contentType,
      [`fields.${field}`]: value,
      include: 2,
      ...options,
    });
    return entries.items;
  } catch (error) {
    console.error(`Error fetching ${contentType} by ${field}:`, error);
    throw error;
  }
};

/**
 * Get image URL from Contentful asset
 * @param {object} asset - Contentful asset object
 * @param {object} options - Image transformation options
 * @returns {string} - Image URL
 */
export const getImageUrl = (asset, options = {}) => {
  if (!asset?.fields?.file?.url) return null;
  
  let url = `https:${asset.fields.file.url}`;
  
  // Add image transformations if provided
  const params = [];
  if (options.width) params.push(`w=${options.width}`);
  if (options.height) params.push(`h=${options.height}`);
  if (options.quality) params.push(`q=${options.quality}`);
  if (options.format) params.push(`fm=${options.format}`);
  if (options.fit) params.push(`fit=${options.fit}`);
  
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  
  return url;
};

export default client;
